import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LockKeyhole } from "lucide-react";
import { useEffect, useState } from "react";

import { libraryApi } from "../../api/collab";
import { Badge, Button, Card, SearchInput, useToast } from "../../ds";

/** "Can't find a report?": search by name, ask for access (an IT admin decides). */
export function RequestAccessCard() {
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setQ(text.trim()), 300);
    return () => window.clearTimeout(t);
  }, [text]);
  const found = useQuery({ queryKey: ["discover", q], queryFn: () => libraryApi.discover(q), enabled: q.length >= 3, retry: false });
  const request = useMutation({
    mutationFn: (id: string) => libraryApi.requestAccess(id),
    onSuccess: () => {
      toast.success("Request sent", "An administrator will review it. You'll be notified.");
      qc.invalidateQueries({ queryKey: ["discover"] });
    },
    onError: (e) => toast.error("Couldn't send the request", e instanceof Error ? e.message : undefined),
  });
  if (found.isError && q.length < 3) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <Card title="Can't find a report?" subtitle="Search by name. If it isn't shared with you, you can ask for access.">
        <SearchInput value={text} onChange={setText} placeholder="Report name (at least 3 letters)" width={320} />
        {q.length >= 3 && found.data ? (
          <div className="ds-stack" style={{ marginTop: 10, gap: 6 }}>
            {found.data.length === 0 ? <div className="ds-small ds-muted">Nothing found that you don't already have.</div> : null}
            {found.data.map((r) => (
              <div key={r.id} className="ds-row" style={{ gap: 10 }}>
                <LockKeyhole size={15} />
                <div className="ds-grow">
                  <strong>{r.name}</strong>
                  <span className="ds-small ds-muted"> {r.owner_name ? `· Owner ${r.owner_name}` : ""}{r.category_name ? ` · ${r.category_name}` : ""}</span>
                </div>
                {r.request_pending ? <Badge tone="warning" dot>Requested</Badge> : <Button size="sm" loading={request.isPending && request.variables === r.id} onClick={() => request.mutate(r.id)}>Request access</Button>}
              </div>
            ))}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
